/**
 * UCM 入向待确认记录详情抽屉
 * 报文头摘要 + 原始报文 + 逐块「当前位置 → 拟改位置」+ 确认结果汇总
 * 支持逐块「写台账 / 忽略 / 改板号 / 快速建档」与整封确认；全程不离开详情。
 *
 * 形态选择：与项目其它详情页一致采用 Drawer（右滑出，宽度约 92vw）。
 * Modal 在长内容下显得局促，且与「列表 → 详情 → 回列表」的导航关系不匹配。
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal, Drawer, Button, Space, Tag, Table, Typography, Descriptions, Alert, Spin, Select, Segmented, Tabs, Tooltip, Input, Form, Collapse, message,
} from 'antd';
import { CheckCircleOutlined, StopOutlined, SyncOutlined, EditOutlined, PlusOutlined, SwapOutlined, ThunderboltOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ucmService, uldService } from '../../../../api/enhancedApi';
import HoldToConfirmButton from '../../../../components/common/HoldToConfirmButton';
import OperationFlightSelector from '../../../../components/common/DataSelectors/OperationFlightSelector';
import UldHistoryTable from '../../UldHistory/components/UldHistoryTable';
import UldDetailModal from '../../UldManagement/components/UldDetailModal';
import {
    UCM_INBOUND_SOURCE,
    UCM_INBOUND_DIRECTION,
    UCM_INBOUND_STATUS,
    UCM_INBOUND_MATCH_STATUS,
    UCM_INBOUND_TRANSPORT_MODE,
    UCM_INBOUND_TRANSPORT_MODE_OPTIONS,
    getTransportMode,
    canConfirmInbound,
    formatInboundLocation,
    formatInboundStatus,
    getBlockStatus,
    isBlockHandled,
    canReopenInbound,
    canReopenInboundItem,
    reopenInboundItemTip,
    UCM_REOPEN_TEXT,
    memberDirectionTag,
    shouldShowSegmentSwitcher,
    isEmptyMember,
    getForceCategory,
} from '../constants/ucmConstants';
import UcmQuickCreateUldModal from './UcmQuickCreateUldModal';

const { Paragraph, Text } = Typography;

const tag = (map, v) => {
    const item = map[v] || {};
    return <Tag color={item.color}>{item.label || v || '-'}</Tag>;
};

const UcmInboundDetailDrawer = ({ visible, pendingId, members, onClose, onChanged, onReopened }) => {
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(false);
    const [detail, setDetail] = useState(null);
    // 确认前预览：逐块处置 + 三色计数（来自 /classify，读实时台账，权威）
    const [classify, setClassify] = useState(null);
    const [flightPickerVisible, setFlightPickerVisible] = useState(false);
    const [pickerSelection, setPickerSelection] = useState([]);
    // 改板号：{ index, value }；快速建档：{ index, identifier, airport }
    const [editTarget, setEditTarget] = useState(null);
    const [createTarget, setCreateTarget] = useState(null);
    // 改判运输方式：null 时不显示弹窗；非空时 = { transport_mode, surface_trip_number, reason }
    const [transportTarget, setTransportTarget] = useState(null);
    // 逐块表横向滚动：把 Drawer 的滚动容器(.ant-drawer-body)交给 Table 的 sticky，
    // 让表头与横向滚动条吸附在可视区底部，无需把抽屉拉到最底才够得到横向滚动条。
    const drawerBodyRef = useRef(null);
    const captureDrawerBody = (node) => {
        drawerBodyRef.current = node && typeof node.closest === 'function'
            ? node.closest('.ant-drawer-body')
            : null;
    };
    // 当前抽屉里正在看哪一段（同源报文里 IN / OUT 切换；prop pendingId 仅作为初始值）
    const [activePendingId, setActivePendingId] = useState(pendingId);
    // 本报文台账变更（H5）：按本报文写成的批次号去 ULD 流转历史里捞；懒加载（首次切到该 Tab 才请求）
    const [ledgerHistory, setLedgerHistory] = useState([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [ledgerLoaded, setLedgerLoaded] = useState(false);
    // 本报文台账变更行里点「查看板」→ 打开 ULD 详情弹窗（H5 报文→板）
    const [uldModal, setUldModal] = useState({ visible: false, uldId: null });
    // 当前活跃段 id 的镜像：异步回调里据此判断"请求返回时是否还停在发起时那一段"，
    // 避免快速切段时旧的 /classify 响应把当前段的预览覆盖错乱（竞态防护）
    const activeIdRef = useRef(activePendingId);
    activeIdRef.current = activePendingId;

    // 段切换器选项：每个 member 一个选项，文案沿用方向多标签的口径
    const segmentOptions = useMemo(() => {
        if (!Array.isArray(members)) return [];
        return members.map((m) => {
            const t = memberDirectionTag(m);
            return { label: t.label, value: m.id };
        });
    }, [members]);

    const activeMember = useMemo(
        () => (Array.isArray(members) ? members.find((m) => m.id === activePendingId) : null),
        [members, activePendingId],
    );

    // 本报文写台账产生的批次号集合：整封 / 逐块 / 强制各一个，记在每块的 block_batch_identifier。
    // 用它去 ULD 流转历史里按 batch 精确捞「本报文落成的台账变更」（H5）。
    const writtenBatches = useMemo(() => {
        const set = new Set();
        (detail?.items || []).forEach((it) => {
            if (it?.block_batch_identifier) set.add(it.block_batch_identifier);
        });
        return Array.from(set);
    }, [detail]);

    const loadLedgerHistory = async () => {
        if (writtenBatches.length === 0) {
            setLedgerHistory([]);
            setLedgerLoaded(true);
            return;
        }
        setLedgerLoading(true);
        try {
            const resp = await uldService.listUldHistory({
                batch_identifiers: writtenBatches.join(','),
                page_size: 200,
                sort_by: 'event_timestamp',
                sort_order: 'desc',
            });
            setLedgerHistory(resp.data?.items || []);
            setLedgerLoaded(true);
        } catch (error) {
            console.error('加载本报文台账变更失败:', error);
            message.error('加载本报文台账变更失败');
        } finally {
            setLedgerLoading(false);
        }
    };

    const handleDrawerTabChange = (key) => {
        if (key === 'ledger' && !ledgerLoaded) loadLedgerHistory();
    };

    // 外部传入的 pendingId 变化（重新打开 / 列表点了另一行）时，同步 active
    useEffect(() => {
        if (visible) setActivePendingId(pendingId);
    }, [visible, pendingId]);

    useEffect(() => {
        if (!visible || !activePendingId) return;
        let cancelled = false;
        setFlightPickerVisible(false);
        setPickerSelection([]);
        setEditTarget(null);
        setCreateTarget(null);
        setClassify(null);
        // 切段 / 重新打开时复位本报文台账变更缓存，避免显示上一段的历史
        setLedgerLoaded(false);
        setLedgerHistory([]);
        (async () => {
            setLoading(true);
            try {
                const resp = await ucmService.getUcmPendingById(activePendingId);
                if (!cancelled) {
                    setDetail(resp.data || null);
                    await loadClassify(resp.data);
                }
            } catch (error) {
                console.error('加载待确认详情失败:', error);
                message.error('加载待确认详情失败');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [visible, activePendingId]);

    // 确认前预览：仅对 pending 记录拉取（已确认/忽略/失败没有"待处理块"，无意义）
    // 时效判断用 activeIdRef（最新活跃段）而非取消标志：异步返回时若已切到别的段
    // （det.id 不再是当前活跃段），丢弃本次结果，避免旧响应覆盖新段的预览（竞态防护）
    const loadClassify = async (det) => {
        if (!det || det.status !== 'pending' || Number(det.uld_total ?? 0) === 0) {
            setClassify(null);
            return;
        }
        try {
            const resp = await ucmService.classifyUcmPending(det.id);
            if (det.id === activeIdRef.current) setClassify(resp.data || null);
        } catch (error) {
            console.error('加载确认前预览失败:', error);
            if (det.id === activeIdRef.current) setClassify(null);
        }
    };

    const reload = async () => {
        try {
            const resp = await ucmService.getUcmPendingById(activePendingId);
            setDetail(resp.data || null);
            await loadClassify(resp.data);
        } catch (error) {
            console.error('刷新待确认详情失败:', error);
        }
    };

    const handleConfirm = () => {
        const items = detail?.items || [];
        const pendingItems = items.filter((it) => !isBlockHandled(it));
        const skipUnknown = pendingItems.filter((it) => !it.known).length;
        const alreadyHandled = items.length - pendingItems.length;
        // 优先用确认前预览的口径（读实时台账，权威）：可直接写 = write；需强制单列
        const counts = classify?.counts;
        const toWrite = counts ? counts.write : pendingItems.filter((it) => it.known).length;
        const forceTotal = counts ? (counts.force_location + counts.force_delivered + counts.force_retired) : 0;
        const filing = counts ? counts.filing : skipUnknown;

        Modal.confirm({
            title: '整封确认写台账',
            content: (
                <div>
                    <div>本次将按报文写台账 <Text strong>{toWrite}</Text> 块（可直接写的已知板）。</div>
                    {forceTotal > 0 && (
                        <div>
                            另有 <Text strong>{forceTotal}</Text> 块过不了常规校验（位置不符 / 已外放 / 退役停用），
                            <Text type="warning">不会随本次写入</Text>；要一次写全，请改用底部「整封强制确认（长按5s）」。
                        </div>
                    )}
                    {filing > 0 && (
                        <div>跳过未知板号 <Text strong>{filing}</Text> 块（不写台账，可先快速建档或改板号）。</div>
                    )}
                    {alreadyHandled > 0 && (
                        <div>已处理的 <Text strong>{alreadyHandled}</Text> 块不受影响。</div>
                    )}
                    <div style={{ marginTop: 6 }}>
                        <Text type="secondary">整封原子化：只有所有块都了结（写入 / 忽略）才会标记为「已确认」；否则保持「待确认」可继续处理。</Text>
                    </div>
                </div>
            ),
            okText: '确认写台账',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const resp = await ucmService.confirmUcmPending(activePendingId);
                    message.success(resp.data?.message || '确认成功，已写台账');
                    await reload();
                    onChanged && onChanged();
                } catch (error) {
                    message.error(error.response?.data?.detail || '确认失败');
                }
            },
        });
    };

    const handleIgnore = async () => {
        setActing(true);
        try {
            const resp = await ucmService.ignoreUcmPending(activePendingId);
            message.success(resp.data?.message || '已忽略');
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '忽略失败');
        } finally {
            setActing(false);
        }
    };

    const handleReopen = () => {
        Modal.confirm({
            title: '重新处理该记录',
            content: UCM_REOPEN_TEXT.recordConfirm,
            okText: '重新处理',
            cancelText: '取消',
            onOk: async () => {
                setActing(true);
                try {
                    const resp = await ucmService.reopenUcmPending(activePendingId);
                    const reopened = resp.data?.data;
                    message.success(resp.data?.message || '已恢复为待处理');
                    await reload();
                    onReopened && onReopened(reopened);
                } catch (error) {
                    message.error(error.response?.data?.detail || '重新处理失败');
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleConfirmItem = async (index) => {
        setActing(true);
        try {
            const resp = await ucmService.confirmUcmPendingItem(activePendingId, index);
            const data = resp.data || {};
            if (data.success === false) {
                message.warning(data.message || '该板未写入台账，请核对后重试');
            } else {
                message.success(data.message || '该板已写入台账');
            }
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '单块写台账失败');
        } finally {
            setActing(false);
        }
    };

    const handleForceConfirmBatch = async (indices) => {
        setActing(true);
        try {
            const resp = await ucmService.forceConfirmUcmPending(activePendingId, indices || []);
            const data = resp.data || {};
            message.success(data.message || '已强制通过');
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '批量强制通过失败');
        } finally {
            setActing(false);
        }
    };

    // 整封强制确认（长按 5s）：一次写正常板 + 强制所有需强制块（位置不符/外放/退役），未知板仍跳过
    const handleConfirmForce = async () => {
        setActing(true);
        try {
            const resp = await ucmService.confirmUcmPending(activePendingId, { force: true });
            const data = resp.data || {};
            message.success(data.message || '已整封强制确认并写台账');
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '整封强制确认失败');
        } finally {
            setActing(false);
        }
    };

    const handleIgnoreItem = async (index) => {
        setActing(true);
        try {
            const resp = await ucmService.ignoreUcmPendingItem(activePendingId, index);
            message.success(resp.data?.message || '该板已忽略');
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '单块忽略失败');
        } finally {
            setActing(false);
        }
    };

    const handleReopenItem = async (index) => {
        setActing(true);
        try {
            const resp = await ucmService.reopenUcmPendingItem(activePendingId, index);
            const reopened = resp.data?.data;
            message.success(resp.data?.message || '已撤销该板忽略');
            await reload();
            onReopened && onReopened(reopened);
        } catch (error) {
            message.error(error.response?.data?.detail || '撤销单块忽略失败');
        } finally {
            setActing(false);
        }
    };

    const handleSaveIdentifier = async () => {
        if (!editTarget) return;
        const val = (editTarget.value || '').trim().toUpperCase();
        if (!val) {
            message.warning('请输入新板号');
            return;
        }
        setActing(true);
        try {
            await ucmService.updateUcmPendingItemIdentifier(activePendingId, editTarget.index, val);
            message.success('板号已更新');
            setEditTarget(null);
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '改板号失败');
        } finally {
            setActing(false);
        }
    };

    const handleQuickCreated = async (identifier) => {
        if (!createTarget) return;
        try {
            await ucmService.updateUcmPendingItemIdentifier(activePendingId, createTarget.index, identifier);
        } catch (error) {
            console.error('建档后刷新该块失败:', error);
        }
        await reload();
        onChanged && onChanged();
    };

    const handleRematchAuto = async () => {
        setActing(true);
        try {
            const resp = await ucmService.rematchUcmPending(activePendingId);
            const next = resp.data?.data;
            message.success(
                next?.match_status === 'matched'
                    ? '已匹配到航班，可确认写台账'
                    : (resp.data?.message || '已重新匹配，但仍未唯一匹配到航班'),
            );
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '重新匹配失败');
        } finally {
            setActing(false);
        }
    };

    // 改判运输方式：把当前记录从 AIR / SURFACE / UNDECIDED 切到另一种
    const openTransportModeModal = () => {
        setTransportTarget({
            transport_mode: (detail?.transport_mode || 'AIR').toUpperCase(),
            surface_trip_number: detail?.surface_trip_number || '',
            reason: '',
        });
    };

    const handleSaveTransportMode = async () => {
        if (!transportTarget) return;
        const mode = (transportTarget.transport_mode || '').toUpperCase();
        if (!mode) {
            message.warning('请选择运输方式');
            return;
        }
        if (mode === 'SURFACE' && !(transportTarget.surface_trip_number || '').trim()) {
            message.warning('卡车转运需要填写趟次号');
            return;
        }
        setActing(true);
        try {
            await ucmService.updateUcmPendingTransportMode(activePendingId, {
                transportMode: mode,
                surfaceTripNumber: mode === 'SURFACE' ? (transportTarget.surface_trip_number || '').trim() : null,
                reason: (transportTarget.reason || '').trim() || null,
            });
            message.success(`已改判为 ${UCM_INBOUND_TRANSPORT_MODE[mode]?.label || mode}`);
            setTransportTarget(null);
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '改判失败');
        } finally {
            setActing(false);
        }
    };

    const openFlightPicker = () => {
        setPickerSelection([]);
        setFlightPickerVisible(true);
    };

    const handleAssignFlight = async () => {
        const flight = pickerSelection[0];
        if (!flight?.id) return;
        setActing(true);
        try {
            await ucmService.rematchUcmPending(activePendingId, flight.id);
            setFlightPickerVisible(false);
            setPickerSelection([]);
            message.success(`已改用航班 ${flight.flight_number || flight.id}，可确认写台账`);
            await reload();
            onChanged && onChanged();
        } catch (error) {
            message.error(error.response?.data?.detail || '指定航班失败');
        } finally {
            setActing(false);
        }
    };

    const matched = detail?.match_status === 'matched';
    const recordEditable = detail?.status === 'pending';
    const transportMode = (detail?.transport_mode || 'AIR').toUpperCase();
    const isSurface = transportMode === 'SURFACE';
    const isUndecided = transportMode === 'UNDECIDED';
    // 卡车转运 / 待判定时，"已知板"单块写台账的条件：
    // SURFACE 要求趟次号；UNDECIDED 不允许；AIR 仍要求匹配航班
    const itemConfirmable = isSurface
        ? !!(detail?.surface_trip_number && String(detail.surface_trip_number).trim())
        : (!isUndecided && matched);
    const itemConfirmTip = isUndecided
        ? '请先改判为航班或卡车转运'
        : (isSurface ? (itemConfirmable ? '' : '缺趟次号，请先改判时补全') : (matched ? '' : '请先匹配航班'));

    // 确认前预览：index → 单块处置（write / needs_force / filing / done），来自 /classify
    const classifyByIndex = useMemo(() => {
        const map = {};
        (classify?.blocks || []).forEach((b) => { map[b.index] = b; });
        return map;
    }, [classify]);
    const forceCounts = classify?.counts || null;
    const needsForceIndices = useMemo(
        () => (classify?.blocks || []).filter((b) => b.outcome === 'needs_force').map((b) => b.index),
        [classify],
    );

    // 快速建档的默认入库机场：进港优先目的站，其次报文头始发站
    const defaultCreateAirport = (item) =>
        item?.target_location?.airport || detail?.origin_airport || '';

    const renderItemActions = (_, record) => {
        const idx = record._k;
        if (record?.block_status === 'ignored') {
            const reopenable = canReopenInboundItem(detail, record);
            return (
                <Tooltip title={reopenInboundItemTip(detail, record)}>
                    <Button
                        size="small"
                        type="link"
                        icon={<UndoOutlined />}
                        disabled={!reopenable}
                        loading={acting}
                        onClick={() => handleReopenItem(idx)}
                    >
                        {UCM_REOPEN_TEXT.itemAction}
                    </Button>
                </Tooltip>
            );
        }
        if (!recordEditable) return <Text type="secondary">—</Text>;
        if (isBlockHandled(record)) return <Text type="secondary">已处理</Text>;
        const cb = classifyByIndex[idx];
        const needsForce = cb?.outcome === 'needs_force';
        return (
            <Space size={4} wrap>
                {record.known ? (
                    // 需强制的块不提供行内单块强制按钮：多块需强制是常态（台账漂移本来就是
                    // 这个功能要纠正的），满屏实心红长按钮会稀释警示且逐块长按不可用；
                    // 统一走预览区「强制通过全部」或底部「整封强制确认」批量放行。
                    // 归宿列的「需强制·类别」标签（含原因 Tooltip）负责解释这一块为什么写不了。
                    !needsForce && (
                        <Tooltip title={itemConfirmTip}>
                            <Button
                                size="small"
                                type="link"
                                icon={<CheckCircleOutlined />}
                                disabled={!itemConfirmable}
                                loading={acting}
                                onClick={() => handleConfirmItem(idx)}
                            >
                                写台账
                            </Button>
                        </Tooltip>
                    )
                ) : (
                    <Button
                        size="small"
                        type="link"
                        icon={<PlusOutlined />}
                        loading={acting}
                        onClick={() => setCreateTarget({
                            index: idx,
                            identifier: record.uld_identifier,
                            airport: defaultCreateAirport(record),
                        })}
                    >
                        快速建档
                    </Button>
                )}
                <Button
                    size="small"
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setEditTarget({ index: idx, value: record.uld_identifier })}
                >
                    改板号
                </Button>
                <Button size="small" type="link" danger loading={acting} onClick={() => handleIgnoreItem(idx)}>
                    忽略
                </Button>
            </Space>
        );
    };

    const itemColumns = [
        {
            title: '板号',
            dataIndex: 'uld_identifier',
            key: 'uld_identifier',
            width: 130,
            render: (v, r) =>
                editTarget && editTarget.index === r._k ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Input
                            size="small"
                            autoFocus
                            value={editTarget.value}
                            maxLength={10}
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => setEditTarget({ ...editTarget, value: e.target.value })}
                            onPressEnter={handleSaveIdentifier}
                        />
                        <Space size={4}>
                            <Button size="small" type="primary" loading={acting} onClick={handleSaveIdentifier}>存</Button>
                            <Button size="small" onClick={() => setEditTarget(null)}>取消</Button>
                        </Space>
                    </div>
                ) : (
                    <Space direction="vertical" size={0}>
                        <span>{v}</span>
                        {/* 板号兜底补全（对方报文把 UTA 漏写成 UT）：明示与原文的差异，可追溯 */}
                        {r.identifier_autofixed_from && (
                            <Tooltip title={`报文原文为 ${r.identifier_autofixed_from}，所属人代码缺尾字母，已按台账兜底补全为 ${v}`}>
                                <Tag color="blue" style={{ marginTop: 2 }}>补全自 {r.identifier_autofixed_from}</Tag>
                            </Tooltip>
                        )}
                    </Space>
                ),
        },
        {
            title: '台账内',
            dataIndex: 'known',
            key: 'known',
            width: 80,
            render: (k) => (k ? <Tag color="green">已知</Tag> : <Tag color="warning">未知</Tag>),
        },
        {
            title: '归宿',
            key: 'block_status',
            width: 140,
            render: (_, r) => {
                const bs = getBlockStatus(r);
                // 待处理块：用确认前预览给出"会怎样"——可直接写 / 需强制（按类别）/ 待建档
                const cb = !isBlockHandled(r) ? classifyByIndex[r._k] : null;
                const forceCat = cb?.outcome === 'needs_force' ? getForceCategory(cb.category) : null;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={bs.color}>{bs.label}</Tag>
                        {forceCat && (
                            <Tooltip title={(cb.reasons || []).join('；') || forceCat.hint}>
                                <Tag color={forceCat.color}>需强制·{forceCat.label}</Tag>
                            </Tooltip>
                        )}
                        {cb?.outcome === 'write' && <Tag color="green">可直接写</Tag>}
                        {cb?.outcome === 'filing' && <Tag color="orange">待建档</Tag>}
                        {r.block_note && <Text type="secondary" style={{ fontSize: 12 }}>{r.block_note}</Text>}
                    </Space>
                );
            },
        },
        {
            title: '当前位置',
            dataIndex: 'current_location',
            key: 'current_location',
            width: 180,
            render: (loc, r) => (r.known ? formatInboundLocation(loc) : '—'),
        },
        {
            title: '拟改位置',
            dataIndex: 'target_location',
            key: 'target_location',
            width: 200,
            render: (loc, r) => (r.known ? <Text type="success">{formatInboundLocation(loc)}</Text> : '—'),
        },
        {
            title: '当前状态',
            dataIndex: 'current_status',
            key: 'current_status',
            width: 130,
            render: (s, r) => (r.known ? formatInboundStatus(s) : '—'),
        },
        {
            title: '拟改状态',
            dataIndex: 'target_status',
            key: 'target_status',
            width: 130,
            render: (s, r) => (r.known ? <Text type="success">{formatInboundStatus(s)}</Text> : '—'),
        },
        { title: '卸载站', dataIndex: 'unload_station', key: 'unload_station', width: 80, render: (v) => v || '-' },
        {
            // 卡车转运常见：板号尾缀里就是接力的航班号；展示出来便于事后追溯卡车后接的航班
            title: '连接航班',
            dataIndex: 'connecting_flight',
            key: 'connecting_flight',
            width: 110,
            render: (v) => v || '-',
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 300,
            render: renderItemActions,
        },
    ];

    const confirmable = canConfirmInbound(detail);
    const ignorable = detail && (detail.status === 'pending' || detail.status === 'parse_failed');
    const cr = detail?.confirm_result;
    // 当前段是不是"空段"（板数为 0；典型是经停里 IN 段的 `.N`）
    // 空段没板可写台账 / 改板号 / 改判运输方式都没意义，底部按钮全部隐藏，并在内容区给一句明确提示
    // 优先看列表传过来的 member（响应快、组里 members 数组已含板数）；
    // member 不在（被刷新前的 detail 还在）时退化到 detail.uld_total
    const emptySegment = activeMember
        ? isEmptyMember(activeMember)
        : (detail ? Number(detail.uld_total ?? 0) === 0 : false);
    const showSwitcher = shouldShowSegmentSwitcher(members);

    // Drawer 的 footer 接受 ReactNode；把动作按钮放右侧、关闭放左侧，保持与同类详情抽屉一致
    // 空段：底部业务按钮全部隐藏，只保留"关闭"——按了也没意义还容易误操作（规格 S2.3）
    const footerNode = (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={onClose}>关闭</Button>
            <Space>
                {canReopenInbound(detail) && (
                    <Tooltip title={UCM_REOPEN_TEXT.recordConfirm}>
                        <Button icon={<UndoOutlined />} loading={acting} onClick={handleReopen}>
                            {UCM_REOPEN_TEXT.recordAction}
                        </Button>
                    </Tooltip>
                )}
                {!emptySegment && (
                    <>
                    {ignorable && (
                        <Button danger icon={<StopOutlined />} loading={acting} onClick={handleIgnore}>
                            整封忽略
                        </Button>
                    )}
                    {recordEditable && (
                        <Tooltip title="把整封报文从 航班 / 卡车转运 / 待人工判定 之间互改">
                            <Button icon={<SwapOutlined />} onClick={openTransportModeModal}>
                                改判运输方式
                            </Button>
                        </Tooltip>
                    )}
                    {confirmable && needsForceIndices.length === 0 && (
                        <Button type="primary" icon={<CheckCircleOutlined />} loading={acting} onClick={handleConfirm}>
                            {isSurface ? '确认（在途·卡车）' : '整封确认写台账'}
                        </Button>
                    )}
                    {confirmable && needsForceIndices.length > 0 && (
                        <Tooltip title="整封确认：正常板直接写 + 位置不符 / 已外放 / 退役停用的问题板一并强制写入（自动跳一致性 / 撤外放 / 恢复在库）；未知板仍需先建档">
                            <HoldToConfirmButton
                                active={!!visible}
                                loading={acting}
                                icon={<ThunderboltOutlined />}
                                minWidth={228}
                                onConfirm={handleConfirmForce}
                            >
                                {`整封强制确认（长按5s·含${needsForceIndices.length}问题块）`}
                            </HoldToConfirmButton>
                        </Tooltip>
                    )}
                    </>
                )}
            </Space>
        </Space>
    );

    return (
        <Drawer
            title="UCM 入向解析详情"
            open={visible}
            onClose={onClose}
            placement="right"
            // 大屏给到接近全屏，小屏 92vw 自适应；与其它详情抽屉一致
            width="min(1280px, 92vw)"
            destroyOnClose
            footer={footerNode}
            styles={{ body: { paddingTop: 12 } }}
        >
            <Spin spinning={loading}>
                {detail && (
                    <Space ref={captureDrawerBody} direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* 同源报文有多段时，顶部用 Segmented 在 IN / OUT 段之间切换；
                            只有一段时不渲染切换器，外观与原实现一致（规格 S2.4，零回归）。
                            选项文案沿用方向多标签的口径，便于眼睛对齐列表行。 */}
                        {showSwitcher && (
                            <Segmented
                                size="small"
                                value={activePendingId}
                                options={segmentOptions}
                                onChange={(val) => setActivePendingId(val)}
                            />
                        )}

                        {/* 空段（板数=0；典型是经停里 IN 段的 `.N`）：给出明确的"这班没板要卸"提示，
                            避免运营对着空表犹豫"是数据丢了还是真的没卸"（规格 S2.3）。 */}
                        {emptySegment && (
                            <Alert
                                type="info"
                                showIcon
                                message={`这班到 ${detail.origin_airport || '本站'} 时没板要卸`}
                                description={
                                    <Text type="secondary">
                                        报文里这一段写的是 `.N`（无板要卸的占位），系统记录在案、不需要写台账。
                                        若需要查看完整原文请看下面"原始报文" Tab；若需要确认 / 改判，请用顶部切换器切到另一段。
                                    </Text>
                                }
                            />
                        )}

                        {/* 顶部只保留运营确认时真正要扫一眼的信息；来源、解析时间和判定依据降级到可展开区。 */}
                        <div
                            style={{
                                padding: '14px 16px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                background: '#fafafa',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 12,
                                }}
                            >
                                <Space direction="vertical" size={8}>
                                    <Space size={8} wrap>
                                        <Text strong style={{ fontSize: 20, lineHeight: 1.2 }}>
                                            {isSurface
                                                ? (detail.surface_trip_number || detail.flight_number || '未填写趟次号')
                                                : (detail.flight_number || '未识别航班号')}
                                        </Text>
                                        {detail.flight_date && (
                                            <Text type="secondary">{dayjs(detail.flight_date).format('YYYY-MM-DD')}</Text>
                                        )}
                                        {detail.direction && tag(UCM_INBOUND_DIRECTION, detail.direction)}
                                        {tag(UCM_INBOUND_STATUS, detail.status)}
                                        {(() => {
                                            const mode = getTransportMode(detail);
                                            return <Tag color={mode.color}>{mode.label}</Tag>;
                                        })()}
                                        {!isSurface && !isUndecided && tag(UCM_INBOUND_MATCH_STATUS, detail.match_status)}
                                    </Space>

                                    <Space size={18} wrap split={<Text type="secondary">·</Text>}>
                                        <Text type="secondary">
                                            报告站 <Text strong>{detail.origin_airport || '-'}</Text>
                                        </Text>
                                        <Text type="secondary">
                                            已识别板 <Text strong>{detail.uld_known}</Text> / {detail.uld_total}
                                        </Text>
                                        {detail.uld_unknown > 0 && <Tag color="orange">未知 {detail.uld_unknown}</Tag>}
                                    </Space>
                                </Space>

                                {recordEditable && !isUndecided && matched && (
                                    <Button size="small" icon={<SearchOutlined />} onClick={openFlightPicker}>
                                        更换航班
                                    </Button>
                                )}
                            </div>

                            <Collapse
                                ghost
                                size="small"
                                style={{ marginTop: 4, marginInline: -8 }}
                                items={[{
                                    key: 'parse-meta',
                                    label: <Text type="secondary">解析信息</Text>,
                                    children: (
                                        <Descriptions size="small" column={2} colon={false}>
                                            <Descriptions.Item label="来源">{tag(UCM_INBOUND_SOURCE, detail.source)}</Descriptions.Item>
                                            <Descriptions.Item label="解析时间">
                                                {detail.created_at ? dayjs(detail.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                            </Descriptions.Item>
                                            {detail.transport_mode_reason && (
                                                <Descriptions.Item label="判定 / 改判依据" span={2}>
                                                    <Text type="secondary">{detail.transport_mode_reason}</Text>
                                                </Descriptions.Item>
                                            )}
                                        </Descriptions>
                                    ),
                                }]}
                            />
                        </div>

                        {/* 待人工判定：必须先改判才能确认（空段时无意义，整组提示隐藏） */}
                        {!emptySegment && isUndecided && detail.status === 'pending' && (
                            <Alert
                                type="warning"
                                showIcon
                                message="运输方式还没定（待人工判定），暂不能写台账"
                                description={
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        <Text type="secondary">
                                            {/* 定性只按航班号查存在性（不带日期）：能走到 UNDECIDED 说明这个号
                                                既不是纯数字趟次号、航班库里也从没出现过（当班没录入的情况已直接判为航班，
                                                走"未匹配到航班"的重新匹配流程，不会落到这里） */}
                                            报文里的"航班号/趟次号" {detail.flight_number ? `「${detail.flight_number}」` : ''}
                                            既不是纯数字趟次号，这个号在航班库里也从没出现过；请用「改判运输方式」选择「航班」（同时补全航班数据）或「卡车转运」（同时补全趟次号）。
                                        </Text>
                                        <Button type="primary" ghost icon={<SwapOutlined />} onClick={openTransportModeModal}>
                                            改判运输方式
                                        </Button>
                                    </Space>
                                }
                            />
                        )}

                        {/* 卡车转运：缺趟次号时给出明确提示（空段时不需要写台账，无意义） */}
                        {!emptySegment && isSurface && detail.status === 'pending' && !(detail.surface_trip_number && String(detail.surface_trip_number).trim()) && (
                            <Alert
                                type="warning"
                                showIcon
                                message="卡车转运缺趟次号，暂不能写台账"
                                description={
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        <Text type="secondary">
                                            ULD 写「在途·卡车」需要趟次号作为运输标识；请用「改判运输方式」补全趟次号。
                                        </Text>
                                        <Button type="primary" ghost icon={<SwapOutlined />} onClick={openTransportModeModal}>
                                            改判运输方式
                                        </Button>
                                    </Space>
                                }
                            />
                        )}

                        {!emptySegment && !isSurface && !isUndecided && detail.match_status !== 'matched' && detail.status === 'pending' && (
                            <Alert
                                type="warning"
                                showIcon
                                message="未唯一匹配到航班，暂不能写台账"
                                description={
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        <Text type="secondary">
                                            可在「航班运行」补全/校正航班记录后点「重新匹配」；若系统仍匹配不到、但你确知是哪一班，可手动指定后再确认。
                                        </Text>
                                        <Space wrap>
                                            <Button icon={<SyncOutlined />} loading={acting} onClick={handleRematchAuto}>
                                                重新匹配
                                            </Button>
                                            <Button
                                                icon={<SearchOutlined />}
                                                onClick={openFlightPicker}
                                            >
                                                搜索指定航班
                                            </Button>
                                        </Space>
                                    </Space>
                                }
                            />
                        )}

                        {/* 确认前预览（三色）：在写台账前先把"会怎样"摆出来——可直接写 / 需强制（按类别）/
                            待建档 / 已了结。需强制的块统一批量放行（此处「强制通过全部」只强制问题块，
                            或底部「整封强制确认」连正常块一起写）；不提供行内单块强制——多块需强制是常态，
                            满屏红长按钮反而稀释警示（规格 E1/E2 调整）。整个抽屉只保留底部整封强制一个实心红钮。
                            仅在记录可确认（已匹配航班 / 有趟次号）时展示，否则交给上面的引导 Alert。 */}
                        {!emptySegment && recordEditable && itemConfirmable && classify && forceCounts && (
                            <Alert
                                type={needsForceIndices.length > 0 ? 'warning' : 'info'}
                                showIcon
                                message={
                                    <Space wrap size={8}>
                                        <Text strong>确认前预览</Text>
                                        <Tag color="green">可直接写 {forceCounts.write}</Tag>
                                        {forceCounts.force_location > 0 && <Tag color="gold">位置不符 {forceCounts.force_location}</Tag>}
                                        {forceCounts.force_delivered > 0 && <Tag color="volcano">已外放 {forceCounts.force_delivered}</Tag>}
                                        {forceCounts.force_retired > 0 && <Tag color="magenta">退役/停用/丢失 {forceCounts.force_retired}</Tag>}
                                        {forceCounts.filing > 0 && <Tag color="orange">待建档 {forceCounts.filing}</Tag>}
                                        {forceCounts.done > 0 && <Tag>已了结 {forceCounts.done}</Tag>}
                                    </Space>
                                }
                                description={
                                    needsForceIndices.length > 0 ? (
                                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                            <Text type="secondary">
                                                有 {needsForceIndices.length} 块过不了常规校验（位置不符 / 已外放 / 退役停用丢失），
                                                逐块原因见「归宿」列的标签。可在此一次性强制通过（不动可直接写的块），
                                                或用底部「整封强制确认」连正常块一起写：强制会自动跳一致性 / 撤外放 / 恢复在库，并逐块留痕。
                                            </Text>
                                            {/* 非实心样式（红字描边）：与底部「整封强制确认」的实心红拉开视觉层级，
                                                全抽屉只保留底部那一个实心红钮 */}
                                            <HoldToConfirmButton
                                                type="default"
                                                active={!!visible}
                                                disabled={acting}
                                                loading={acting}
                                                icon={<ThunderboltOutlined />}
                                                onConfirm={() => handleForceConfirmBatch(needsForceIndices)}
                                            >
                                                {`长按 5s 强制通过全部 ${needsForceIndices.length} 块`}
                                            </HoldToConfirmButton>
                                        </Space>
                                    ) : (
                                        <Text type="secondary">
                                            {forceCounts.filing > 0
                                                ? `全部可直接写或已了结；另有 ${forceCounts.filing} 块未知板需先快速建档 / 改板号。`
                                                : '全部可直接写或已了结，点底部「整封确认写台账」即可。'}
                                        </Text>
                                    )
                                }
                            />
                        )}

                        {cr && (() => {
                            const skippedList = Array.isArray(cr.skipped) ? cr.skipped : [];
                            const reviewList = Array.isArray(cr.needs_review) ? cr.needs_review : [];
                            const failedList = Array.isArray(cr.failed) ? cr.failed : [];
                            const forceList = Array.isArray(cr.needs_force) ? cr.needs_force : [];
                            const skippedCount = cr.skipped_count ?? (skippedList.length || cr.skipped_unknown || 0);
                            const reviewCount = cr.needs_review_count ?? reviewList.length;
                            const failedCount = cr.failed_count ?? (Array.isArray(cr.failed) ? cr.failed.length : Number(cr.failed) || 0);
                            const forceCount = cr.needs_force_count ?? forceList.length;
                            const alertType = failedCount > 0 ? 'error' : (reviewCount > 0 || forceCount > 0) ? 'warning' : 'success';
                            const renderGroup = (label, list) =>
                                list.length > 0 ? (
                                    <div style={{ marginTop: 4 }}>
                                        <Text type="secondary">{label}：</Text>
                                        {list.map((f, i) => (
                                            <div key={i}>
                                                <Text code>{f.uld_identifier}</Text>：{f.reason}
                                            </div>
                                        ))}
                                    </div>
                                ) : null;
                            const hasDetail = skippedList.length || reviewList.length || failedList.length || forceList.length;
                            const forceMsg = forceCount > 0 ? `，需强制 ${forceCount} 块` : '';
                            return (
                                <Alert
                                    type={alertType}
                                    showIcon
                                    message={`确认结果：写入 ${cr.written} 块${forceMsg}，跳过 ${skippedCount} 块，待复核 ${reviewCount} 块，失败 ${failedCount} 块`}
                                    description={
                                        hasDetail ? (
                                            <div style={{ maxHeight: 200, overflow: 'auto' }}>
                                                {renderGroup('需强制通过（位置不符 / 已外放 / 退役停用，长按 5s 可放行）', forceList)}
                                                {renderGroup('跳过（未知板号 / 已外放 / 已停用 / 已忽略）', skippedList)}
                                                {renderGroup('待人工复核（位置与航班始发/目的站不一致）', reviewList)}
                                                {renderGroup('失败（请稍后重试）', failedList)}
                                                {cr.batch_identifier && (
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text type="secondary">批次号 {cr.batch_identifier}</Text>
                                                    </div>
                                                )}
                                            </div>
                                        ) : cr.batch_identifier ? `批次号 ${cr.batch_identifier}` : null
                                    }
                                />
                            );
                        })()}

                        <Tabs
                            size="small"
                            // 空段：用 key 强制重渲染并锁定到原文 Tab（这里通过 key 实现"切到空段时 Tabs 重建"），
                            // 其它段保持 uncontrolled、defaultActiveKey=items 的原行为，零回归
                            key={emptySegment ? 'empty' : 'normal'}
                            defaultActiveKey={emptySegment ? 'raw' : 'items'}
                            onChange={handleDrawerTabChange}
                            items={[
                                // 逐块表只在有板时展示——空段没板可看，列出来反而引人误会
                                ...(!emptySegment ? [{
                                    key: 'items',
                                    label: `逐块「当前位置 → 拟改位置」（${detail.uld_total || 0}）`,
                                    children: (
                                        <Table
                                            columns={itemColumns}
                                            dataSource={(detail.items || []).map((it, idx) => ({ ...it, _k: idx }))}
                                            rowKey="_k"
                                            size="small"
                                            pagination={false}
                                            scroll={{ x: 'max-content' }}
                                            sticky={{ getContainer: () => drawerBodyRef.current || window }}
                                        />
                                    ),
                                }] : []),
                                {
                                    key: 'raw',
                                    label: '原始报文',
                                    children: (
                                        <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, maxHeight: 480, overflowY: 'auto' }}>
                                            <Paragraph
                                                code
                                                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 0, fontFamily: 'Courier New, monospace' }}
                                            >
                                                {detail.raw_message || '（无原文）'}
                                            </Paragraph>
                                        </div>
                                    ),
                                },
                                // 本报文台账变更（H5）：按本报文写成的批次号捞 ULD 流转历史；每行可点回对应板详情。
                                {
                                    key: 'ledger',
                                    label: `本报文台账变更（${writtenBatches.length > 0 ? ledgerHistory.length : 0}）`,
                                    children: writtenBatches.length === 0 ? (
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="本报文还没写台账"
                                            description={<Text type="secondary">确认 / 逐块写台账 / 强制通过后，这里会列出本报文落成的每条 ULD 变更，并可点回对应板详情。</Text>}
                                        />
                                    ) : (
                                        <UldHistoryTable
                                            embedded
                                            data={ledgerHistory}
                                            loading={ledgerLoading}
                                            pagination={false}
                                            onUldClick={(record) => setUldModal({ visible: true, uldId: record.uld_id })}
                                            scrollY={420}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Space>
                )}
            </Spin>

            <UcmQuickCreateUldModal
                visible={!!createTarget}
                defaultIdentifier={createTarget?.identifier}
                defaultAirport={createTarget?.airport}
                onClose={() => setCreateTarget(null)}
                onCreated={handleQuickCreated}
            />

            {/* 改判运输方式：用小 Modal 收集字段（与抽屉是嵌套关系，强制聚焦操作） */}
            <Modal
                title="改判运输方式"
                open={!!transportTarget}
                onCancel={() => setTransportTarget(null)}
                onOk={handleSaveTransportMode}
                confirmLoading={acting}
                okText="保存改判"
                cancelText="取消"
                destroyOnClose
            >
                {transportTarget && (
                    <Form layout="vertical">
                        <Form.Item label="改判为" required>
                            <Select
                                value={transportTarget.transport_mode}
                                onChange={(v) =>
                                    setTransportTarget({ ...transportTarget, transport_mode: v })
                                }
                                options={UCM_INBOUND_TRANSPORT_MODE_OPTIONS}
                            />
                        </Form.Item>
                        {transportTarget.transport_mode === 'SURFACE' && (
                            <Form.Item
                                label="卡车趟次号"
                                required
                                help="改判为卡车转运时必填；留空则由系统按报文头里的纯数字兜底"
                            >
                                <Input
                                    value={transportTarget.surface_trip_number || ''}
                                    placeholder="如 18154"
                                    onChange={(e) =>
                                        setTransportTarget({
                                            ...transportTarget,
                                            surface_trip_number: e.target.value,
                                        })
                                    }
                                />
                            </Form.Item>
                        )}
                        <Form.Item
                            label="改判依据"
                            help="可选；会写入 transport_mode_reason 供事后核对（如「与一线核对，该 18154 是卡车趟次」）"
                        >
                            <Input.TextArea
                                rows={2}
                                value={transportTarget.reason || ''}
                                onChange={(e) =>
                                    setTransportTarget({ ...transportTarget, reason: e.target.value })
                                }
                            />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* 本报文台账变更行「查看板」→ ULD 详情弹窗（H5 报文→板） */}
            <UldDetailModal
                visible={uldModal.visible}
                uldId={uldModal.uldId}
                onCancel={() => setUldModal({ visible: false, uldId: null })}
            />

            {/* 航班搜索选择器 Modal：用完整的表格式选择器替代原来的 inline Select */}
            <Modal
                title={
                    <Space>
                        <SearchOutlined />
                        <span>搜索指定航班</span>
                        {detail?.flight_number && (
                            <Tag color="processing">报文航班号: {detail.flight_number}</Tag>
                        )}
                        {detail?.flight_date && (
                            <Tag>报文日期: {dayjs(detail.flight_date).format('YYYY-MM-DD')}</Tag>
                        )}
                    </Space>
                }
                open={flightPickerVisible}
                width="min(1100px, 90vw)"
                destroyOnClose
                onCancel={() => setFlightPickerVisible(false)}
                onOk={handleAssignFlight}
                confirmLoading={acting}
                okText={matched ? '改用此航班' : '使用此航班'}
                okButtonProps={{ disabled: pickerSelection.length === 0 || acting }}
                cancelText="取消"
                styles={{ body: { padding: 0, height: '60vh', overflow: 'hidden' } }}
            >
                <OperationFlightSelector
                    mode="single"
                    compact
                    value={pickerSelection}
                    onChange={setPickerSelection}
                    filterParams={detail?.flight_number ? { flight_number: detail.flight_number } : undefined}
                    defaultDateRange={[
                        dayjs.utc(detail?.flight_date || undefined).subtract(7, 'day'),
                        dayjs.utc(detail?.flight_date || undefined).add(7, 'day'),
                    ]}
                    defaultStatus={['SCHEDULED', 'DEPARTED', 'DELAYED', 'COMPLETED', 'DIVERTED']}
                />
            </Modal>
        </Drawer>
    );
};

export default UcmInboundDetailDrawer;
